// ════════════════════════════════════════════
// GATING COMPONENTS — reusable UI for plan/credit gates
// ════════════════════════════════════════════
import { useState, useEffect } from 'react'
import { useGate, planLabel, planColor } from '../utils/gating'
import { useAuth } from '../context/AuthContext'
import { CREDIT_COSTS } from '../utils/config'
import PricingModal from './PricingModal'
import merlinImg from '../assets/merlin.png'
import { Sparkles } from './ui'

// ════════════════════════════════════════════
// LockedFeature — wraps any child with a locked overlay when plan insufficient
// Modes:
//   'blur' → show blurred content + centered unlock CTA
//   'hide' → completely replace content with a teaser card
//   'badge' → show content + a small lock badge in the corner (non-blocking)
// ════════════════════════════════════════════
export function LockedFeature({
  feature,
  creditAction = null,
  mode = 'blur',
  title,
  description,
  children,
  className = '',
}) {
  const gate = useGate(feature, { creditAction })
  const [showPricing, setShowPricing] = useState(false)

  // If access granted, wrap in a plain div so external className (margin/padding) still applies
  if (gate.allowed) return className ? <div className={className}>{children}</div> : <>{children}</>

  const reqPlan = gate.requiredPlan
  const planName = planLabel(reqPlan)
  const color = planColor(reqPlan)
  const defaultTitle = title || (gate.reason === 'credit'
    ? 'Crédits insuffisants'
    : `Fonctionnalité ${planName}`)
  const defaultDesc = description || (gate.reason === 'credit'
    ? `Il te manque des crédits pour cette action. Passe au plan supérieur pour continuer.`
    : `Débloque cette fonctionnalité en passant au plan ${planName}.`)

  // Mode 'hide' → full replacement with teaser card
  if (mode === 'hide') {
    return (
      <>
        <button type="button" onClick={() => setShowPricing(true)}
          className={`w-full rounded-2xl p-6 text-left transition-all hover:scale-[1.01] ${className}`}
          style={{
            background: `linear-gradient(135deg, ${color}15, ${color}08)`,
            border: `2px dashed ${color}40`,
          }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0"
              style={{ background: `${color}20`, color }}>
              🔒
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base font-bold text-ink">{defaultTitle}</span>
                <span className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{ background: color, color: 'white' }}>
                  {planName}
                </span>
              </div>
              <p className="text-sm text-ink-muted leading-relaxed">{defaultDesc}</p>
              <span className="inline-block mt-2 text-sm font-semibold" style={{ color }}>
                Débloquer →
              </span>
            </div>
          </div>
        </button>
        {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
      </>
    )
  }

  // Mode 'badge' → show content with a small lock in corner (non-blocking)
  if (mode === 'badge') {
    return (
      <>
        <div className={`relative ${className}`}>
          {children}
          <button onClick={() => setShowPricing(true)}
            className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full shadow-lg text-xs font-bold uppercase tracking-wider transition-all hover:scale-110"
            style={{ background: color, color: 'white' }}
            title={defaultDesc}>
            🔒 {planName}
          </button>
        </div>
        {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
      </>
    )
  }

  // Mode 'blur' (default) → content visible but blurred with centered CTA
  return (
    <>
      <div className={`relative overflow-hidden rounded-2xl ${className}`}>
        {/* Blurred content */}
        <div className="pointer-events-none select-none" style={{ filter: 'blur(4px) saturate(0.7)', opacity: 0.55 }}>
          {children}
        </div>
        {/* Overlay with CTA */}
        <div className="absolute inset-0 flex items-center justify-center p-4"
          style={{ background: 'linear-gradient(135deg, rgba(15,10,26,0.4) 0%, rgba(15,10,26,0.6) 100%)', backdropFilter: 'blur(2px)' }}>
          <button onClick={() => setShowPricing(true)}
            className="max-w-sm w-full rounded-xl p-4 text-left transition-all hover:scale-[1.02]"
            style={{ background: 'white', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: `${color}20`, color }}>
                🔒
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-ink">{defaultTitle}</span>
                  <span className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                    style={{ background: color, color: 'white' }}>
                    {planName}
                  </span>
                </div>
                <p className="text-sm text-ink-muted leading-relaxed">{defaultDesc}</p>
                <span className="inline-block mt-2 text-sm font-bold" style={{ color }}>
                  Passer au plan {planName} →
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </>
  )
}

// ════════════════════════════════════════════
// BlurLock — display-only blur wrapper with overlay + upgrade CTA
// Contrairement à LockedFeature, ne fait PAS de check useGate — c'est le
// caller qui décide quand appliquer le flou (ex: "1 sur 3 visible, 2 floutés"
// dans une liste de bios). Idéal pour les gatings partiels free-tier.
//
// Props:
//   - locked (bool) : si false, render children direct
//   - label (string) : texte sur l'overlay (ex: "Débloque avec Basic")
//   - upgradeTo ('basic'|'createur'|'pro') : plan suggéré, colore le CTA
//   - dense (bool) : variante compacte (pour items dans une liste)
//   - blurAmount (number, px) : intensité du flou (default 6)
// ════════════════════════════════════════════
export function BlurLock({
  locked = true,
  label = null,
  upgradeTo = 'basic',
  dense = false,
  blurAmount = 6,
  children,
  className = '',
}) {
  const [showPricing, setShowPricing] = useState(false)
  if (!locked) return className ? <div className={className}>{children}</div> : <>{children}</>

  const color = planColor(upgradeTo)
  const planName = planLabel(upgradeTo)
  const defaultLabel = label || `Débloque avec ${planName}`

  return (
    <>
      <button
        type="button"
        onClick={() => setShowPricing(true)}
        className={`relative block w-full overflow-hidden rounded-xl text-left transition-transform hover:scale-[1.01] ${className}`}
        style={{ cursor: 'pointer' }}
      >
        {/* Blurred content */}
        <div
          className="pointer-events-none select-none"
          style={{ filter: `blur(${blurAmount}px) saturate(0.55)`, opacity: 0.5 }}
        >
          {children}
        </div>
        {/* Overlay */}
        <div
          className={`absolute inset-0 flex items-center justify-center ${dense ? 'p-2' : 'p-4'}`}
          style={{
            background: `linear-gradient(135deg, rgba(12,10,32,0.55) 0%, rgba(12,10,32,0.7) 100%)`,
            backdropFilter: 'blur(1px)',
          }}
        >
          <div
            className={`flex items-center gap-2 rounded-full font-semibold shadow-lg ${dense ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'}`}
            style={{
              background: `linear-gradient(135deg, ${color}, ${color}dd)`,
              color: 'white',
            }}
          >
            <span>🔒</span>
            <span>{defaultLabel}</span>
          </div>
        </div>
      </button>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </>
  )
}

// ════════════════════════════════════════════
// FreeQuotaBanner — bandeau inline affiché aux free users quand ils ont
// atteint un quota lifetime. Remplace le contenu habituel par un CTA.
// Props :
//   - action (string) : action quotée (ex: 'script_sonnet', 'chat_message')
//   - title, description : override des textes par défaut
// ════════════════════════════════════════════
export function FreeQuotaBanner({ action, title, description, upgradeTo = 'basic' }) {
  const [showPricing, setShowPricing] = useState(false)
  const color = planColor(upgradeTo)
  const planName = planLabel(upgradeTo)
  const defaultTitle = title || `Tu as utilisé ton essai gratuit`
  const defaultDesc = description || `Passe en ${planName} pour continuer sans limite — tarif béta figé à vie.`

  return (
    <>
      <button
        type="button"
        onClick={() => setShowPricing(true)}
        className="w-full rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
        style={{
          background: `linear-gradient(135deg, ${color}18, ${color}08)`,
          border: `1.5px dashed ${color}55`,
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-lg"
            style={{ background: `${color}25`, color }}
          >
            🎁
          </div>
          <div className="flex-1 min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-sm font-bold text-ink">{defaultTitle}</span>
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                style={{ background: color }}
              >
                {planName}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-ink-muted">{defaultDesc}</p>
            <span className="mt-2 inline-block text-sm font-bold" style={{ color }}>
              Débloquer →
            </span>
          </div>
        </div>
      </button>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </>
  )
}

// ════════════════════════════════════════════
// LockedAction — button / action with lock badge & tooltip
// Replaces the action with a locked-looking button when gated
// ════════════════════════════════════════════
export function LockedAction({
  feature,
  creditAction = null,
  children,
  label = null,
  className = '',
  ...rest
}) {
  const gate = useGate(feature, { creditAction })
  const [showPricing, setShowPricing] = useState(false)
  if (gate.allowed) return <>{children}</>

  const reqPlan = gate.requiredPlan
  const color = planColor(reqPlan)
  const planName = planLabel(reqPlan)

  return (
    <>
      <button type="button" onClick={() => setShowPricing(true)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all hover:scale-[1.02] ${className}`}
        style={{ background: `${color}10`, color, border: `1px solid ${color}40` }}
        title={`Débloquer avec le plan ${planName}`}
        {...rest}>
        🔒 {label || `${planName} requis`}
      </button>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </>
  )
}

// ════════════════════════════════════════════
// CreditQuotaBadge — small badge showing current quota status with color
// Shows warning when usage > 80%, danger when >95%
// ════════════════════════════════════════════
export function CreditQuotaBadge({ compact = false }) {
  const { plan, isUnlimited, maxCredits, monthlyUsage, usagePct } = useAuth()
  if (isUnlimited || maxCredits === -1) return null

  const pct = usagePct()
  const remaining = Math.max(0, maxCredits - monthlyUsage)

  let color = '#22c55e' // green
  let label = 'OK'
  if (pct >= 100) { color = '#ef4444'; label = 'Épuisé' }
  else if (pct >= 95) { color = '#ef4444'; label = 'Critique' }
  else if (pct >= 80) { color = 'var(--merlin-accent-2, #f59e0b)'; label = 'Attention' }

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
        style={{ background: `${color}15`, color }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
        {remaining.toLocaleString()}
      </span>
    )
  }

  return (
    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg text-xs"
      style={{ background: `${color}10`, border: `1px solid ${color}30`, color }}>
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="font-bold">{remaining.toLocaleString()} / {maxCredits.toLocaleString()}</span>
      <span className="opacity-70">·</span>
      <span className="font-semibold uppercase tracking-wider text-xs">{label}</span>
    </div>
  )
}

// ════════════════════════════════════════════
// CreditBlockedState — full card shown when an action is blocked by credits
// Merlin-themed, clear, encouraging, with pricing CTA
// Drop-in replacement for whatever the tool would show if the action succeeded
// ════════════════════════════════════════════
export function CreditBlockedState({
  action,
  title = null,
  description = null,
  compact = false,
}) {
  const { plan, monthlyUsage, maxCredits, remainingCredits, usagePct } = useAuth()
  const [showPricing, setShowPricing] = useState(false)
  const cost = (CREDIT_COSTS && CREDIT_COSTS[action]) || null
  const remaining = remainingCredits()
  const pct = typeof usagePct === 'function' ? usagePct() : 0

  const defaultTitle = title || 'Plus assez de crédits'
  const defaultDesc = description || (cost
    ? `Cette action coûte ${cost} crédits et il t'en reste ${remaining.toLocaleString()}. Passe à un plan supérieur pour débloquer plus de crédits.`
    : `Tu as utilisé tous tes crédits ce mois-ci (${monthlyUsage.toLocaleString()}/${maxCredits.toLocaleString()}). Passe à un plan supérieur pour continuer.`)

  // Compact variant for inline use near buttons
  if (compact) {
    return (
      <>
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.08), rgba(var(--merlin-accent-rgb, 254,130,4),0.05))', border: '1px solid rgba(239,68,68,0.25)' }}>
          <span className="text-sm">⚡</span>
          <span className="text-sm font-semibold text-red-600">
            Crédits insuffisants {cost ? `(${cost} requis, ${remaining.toLocaleString()} restants)` : ''}
          </span>
          <button onClick={() => setShowPricing(true)}
            className="ml-1 px-2.5 py-1 rounded-lg text-xs font-bold text-white transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
            Upgrader →
          </button>
        </div>
        {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
      </>
    )
  }

  // Full card variant — Merlin-themed
  return (
    <>
      <div className="rounded-2xl p-6 text-center overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, rgba(30,17,69,0.95) 0%, rgba(45,27,105,0.95) 40%, rgba(59,31,142,0.95) 100%)',
          border: '1.5px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.35)',
          boxShadow: '0 12px 48px rgba(var(--merlin-accent-rgb, 254,130,4),0.15)',
        }}>
        {/* Sparkles — ambient cosmic particles */}
        <Sparkles count={8} color="var(--merlin-accent, #fe8204)" className="opacity-40" />

        {/* Merlin mascot */}
        <div className="relative z-10 mb-3">
          <img src={merlinImg} alt="Merlin" className="w-16 h-16 mx-auto object-contain"
            style={{ filter: 'drop-shadow(0 0 20px rgba(var(--merlin-accent-rgb, 254,130,4),0.45))' }} />
        </div>

        {/* Text */}
        <h3 className="relative z-10 text-sm font-bold text-white mb-1.5">{defaultTitle}</h3>
        <p className="relative z-10 text-sm leading-relaxed mb-4 max-w-xs mx-auto"
          style={{ color: 'rgba(255,255,255,0.7)' }}>
          {defaultDesc}
        </p>

        {/* Usage bar */}
        <div className="relative z-10 max-w-xs mx-auto mb-4">
          <div className="flex items-center justify-between text-xs mb-1"
            style={{ color: 'rgba(255,255,255,0.5)' }}>
            <span>Crédits ce mois</span>
            <span className="font-mono font-bold" style={{ color: pct >= 95 ? '#ef4444' : 'var(--merlin-accent, #fe8204)' }}>
              {monthlyUsage.toLocaleString()}/{maxCredits.toLocaleString()}
            </span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(100, pct)}%`,
                background: pct >= 95
                  ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                  : 'linear-gradient(90deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                boxShadow: `0 0 8px ${pct >= 95 ? 'rgba(239,68,68,0.5)' : 'rgba(var(--merlin-accent-rgb, 254,130,4),0.5)'}`,
              }} />
          </div>
        </div>

        {/* CTA */}
        <button onClick={() => setShowPricing(true)}
          className="relative z-10 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-[1.03]"
          style={{
            background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
            color: 'white',
            boxShadow: '0 4px 20px rgba(var(--merlin-accent-rgb, 254,130,4),0.4)',
          }}>
          ⚡ Débloquer plus de crédits
        </button>
        <p className="relative z-10 text-xs mt-3" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Ton plan actuel : <strong className="uppercase" style={{ color: 'rgba(255,255,255,0.55)' }}>{plan}</strong>
        </p>
      </div>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </>
  )
}

// ════════════════════════════════════════════
// LowCreditsBanner — sticky top banner when < 10% credits remaining
// Appears globally (used in NavBar / App)
// ════════════════════════════════════════════
export function LowCreditsBanner({ inline = false }) {
  const { plan, isUnlimited, maxCredits, remainingCredits, usagePct } = useAuth()
  const [showPricing, setShowPricing] = useState(false)
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('cg_low_credits_dismissed') === 'true' } catch { return false }
  })

  if (isUnlimited || maxCredits === -1 || dismissed) return null
  const pct = typeof usagePct === 'function' ? usagePct() : 0
  if (pct < 90) return null // only show at 10% or less remaining

  const remaining = remainingCredits()
  const isCritical = pct >= 100
  const isUrgent = pct >= 95

  const handleDismiss = () => {
    setDismissed(true)
    try { sessionStorage.setItem('cg_low_credits_dismissed', 'true') } catch {}
  }

  const outerClass = inline
    ? 'pointer-events-auto animate-fade w-[min(92vw,560px)]'
    : 'fixed top-[56px] left-1/2 -translate-x-1/2 z-[200] animate-fade w-[min(92vw,560px)]'

  return (
    <>
      <div className={outerClass}>
        <div className="flex items-center justify-between gap-3 px-4 py-2 rounded-full shadow-lg text-sm font-semibold"
          style={{
            background: isCritical
              ? 'linear-gradient(135deg, #dc2626, #ef4444)'
              : isUrgent
                ? 'linear-gradient(135deg, #ef4444, #f97316)'
                : 'linear-gradient(135deg, var(--merlin-accent-2, #f59e0b), var(--merlin-accent, #fe8204))',
            color: 'white',
            boxShadow: `0 4px 20px ${isCritical ? 'rgba(220,38,38,0.4)' : 'rgba(var(--merlin-accent-rgb, 254,130,4),0.35)'}`,
          }}>
          {/* Left: icon + message */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex-shrink-0" aria-hidden>{isCritical ? '🚨' : '⚡'}</span>
            <span className="truncate">
              {isCritical
                ? 'Crédits épuisés'
                : `Plus que ${remaining.toLocaleString()} crédits`}
            </span>
            <span className="opacity-75 text-xs hidden sm:inline flex-shrink-0">· {pct}%</span>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowPricing(true)}
              className="px-3 py-1 rounded-full text-xs font-bold transition-all hover:scale-105"
              style={{ background: 'white', color: isCritical ? '#dc2626' : '#b45309' }}>
              Upgrader →
            </button>
            <button onClick={handleDismiss}
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs opacity-75 hover:opacity-100 transition-all"
              style={{ background: 'rgba(255,255,255,0.18)' }}
              title="Masquer jusqu'à la prochaine session"
              aria-label="Masquer">
              ✕
            </button>
          </div>
        </div>
      </div>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </>
  )
}

// ════════════════════════════════════════════
// ConfirmCreditsCost — confirmation modal for expensive bulk actions
// e.g., "Analyser 5 vidéos coutera 150 crédits (30/vidéo). Il t'en reste 200."
// Returns { showConfirm, openConfirm, confirmUI } — caller renders confirmUI and
// triggers openConfirm({ total, unitCost, unitLabel, onConfirm })
// ════════════════════════════════════════════
export function useConfirmCreditsCost() {
  const [config, setConfig] = useState(null)
  const { remainingCredits, isUnlimited } = useAuth()
  const [showPricing, setShowPricing] = useState(false)

  function openConfirm({ action, count, unitLabel, totalCost, onConfirm, title, description }) {
    const unit = totalCost && count ? totalCost / count : (CREDIT_COSTS[action] || 0)
    const total = totalCost != null ? totalCost : unit * (count || 1)
    const remaining = remainingCredits()
    setConfig({ action, count, unitLabel, totalCost: total, unitCost: unit, onConfirm, title, description, remaining })
  }

  function close() { setConfig(null) }

  const confirmUI = config ? (
    <div className="fixed inset-0 z-[950] flex items-center justify-center p-4"
      onClick={close}
      style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="relative max-w-md w-full rounded-2xl overflow-hidden animate-in"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1e1145 0%, #2d1b69 40%, #3b1f8e 100%)',
          border: '1.5px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.3)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}>
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <img src={merlinImg} alt="" className="w-12 h-12 object-contain flex-shrink-0"
              style={{ filter: 'drop-shadow(0 0 15px rgba(var(--merlin-accent-rgb, 254,130,4),0.35))' }} />
            <div className="flex-1">
              <h3 className="text-sm font-bold" style={{ color: '#fff' }}>{config.title || 'Confirmer l\'action'}</h3>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {config.description || `${config.count} ${config.unitLabel || 'items'} à traiter`}
              </p>
            </div>
          </div>

          {/* Cost breakdown */}
          <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.2)' }}>
            <div className="flex items-center justify-between text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
              <span>Coût total</span>
              <span className="font-bold text-orange">{Math.ceil(config.totalCost)} crédits</span>
            </div>
            {config.count > 1 && (
              <div className="flex items-center justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <span>{Math.ceil(config.unitCost)} crédits × {config.count}</span>
                <span></span>
              </div>
            )}
            <div className="h-px my-2" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <div className="flex items-center justify-between text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
              <span>Crédits restants</span>
              <span className="font-bold" style={{ color: config.remaining >= config.totalCost ? '#22c55e' : '#ef4444' }}>
                {config.remaining === Infinity ? '∞' : config.remaining.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
              <span>Après cette action</span>
              <span className="font-mono">
                {config.remaining === Infinity ? '∞' : Math.max(0, config.remaining - config.totalCost).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          {config.remaining >= config.totalCost || isUnlimited ? (
            <div className="flex items-center gap-2">
              <button onClick={close}
                className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all hover:bg-white/5"
                style={{ color: 'rgba(255,255,255,0.6)' }}>
                Annuler
              </button>
              <button onClick={() => { const cb = config.onConfirm; close(); cb && cb() }}
                className="flex-1 px-4 py-2.5 text-sm font-bold rounded-xl text-white transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 4px 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.4)' }}>
                Confirmer ({Math.ceil(config.totalCost)} cr)
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-center px-3 py-2 rounded-lg"
                style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
                ⚡ Il te manque {Math.ceil(config.totalCost - config.remaining).toLocaleString()} crédits pour cette action
              </p>
              <div className="flex items-center gap-2">
                <button onClick={close}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all hover:bg-white/5"
                  style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Annuler
                </button>
                <button onClick={() => { close(); setShowPricing(true) }}
                  className="flex-1 px-4 py-2.5 text-sm font-bold rounded-xl text-white transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 4px 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.4)' }}>
                  Débloquer plus →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} open={true} />}
    </div>
  ) : null

  return { openConfirm, confirmUI, isOpen: !!config }
}


